import React, { useEffect } from "react";
import { useNetwork } from "../../hooks/useNetwork";
import { useSelectedNetworkContext } from "../../contexts/NetworkContext/context";
import { useIntegrationContext } from "./IntegrationProvider";
import { EnhancedTableBody } from "../EnhancedTableBody";
import { IntegrationTableKineticBodyRow } from "./IntegrationTableKineticBodyRow";
import { usePageTablePaginationContext } from "../PageTablePagination/context";
import { usePaginationHandler } from "../../hooks/usePaginationHandler";
import { IIntegrationsKinetic } from "../../services/IntegrationApi";
import { useGetRoomSyncFloorPlan } from "./useGetRoomSyncFloorPlan";

interface IProps {
  showAll?: boolean;
}

// Convert API data to table format
const convertKineticIntegrationToTableRow = (
  integration: IIntegrationsKinetic,
  getL2: any,
  getRoomSyncFloorPlan: (row: IIntegrationsKinetic) => string | undefined
) => {
  // const l2 = getL2(integration.l1Id, integration.l2Id);
  return {
    id: integration.id.toString(),
    l1Id: integration.l1Id,
    l2Id: integration.l2Id,
    roomsyncFloorPlan: getRoomSyncFloorPlan(integration) || `L2-${integration.l2Id}`,
    instanceId: integration.instanceId,
    enabledAccess: integration.enableAccess ? "Yes" : "No",
    mappingType: integration.networkMappingType,
    yearOfStudy: integration.yearOfStudyId,
    studentType: integration.studentType,
    pathway: integration.pathwayId,
    importFieldSelection: integration.importFieldSelection,
  };
};

export const IntegrationTableKineticBody: React.FC<IProps> = (props) => {
  const [filteredIntegrations, setFilteredIntegrations] = React.useState<any[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const { getL2 } = useNetwork();
  const getRoomSyncFloorPlan = useGetRoomSyncFloorPlan();
  const { selectedNetworkId } = useSelectedNetworkContext();
  const { integrations, loadIntegrations } = useIntegrationContext();
  const { page, setPage, perPage, setPages } = usePageTablePaginationContext();
  const { selectedRows, pages } = usePaginationHandler(
    filteredIntegrations,
    page,
    perPage
  );

  const showRow = (l1Id: number, l2Id: number) => {
    if(props.showAll) {
      return true;
    }
    return getL2(l1Id, l2Id)?.matchingRoundStatus === "MATCHING" || getL2(l1Id, l2Id)?.matchingRoundStatus === "UPCOMING";
  };

  useEffect(() => {
    // Convert API integrations to table format and filter
    const kineticIntegrations = integrations
      .filter((integration: any) => integration.networkMappingType === "Kinetic")
      .map((integration: any) => convertKineticIntegrationToTableRow(integration, getL2, getRoomSyncFloorPlan))
      .filter((i: any) => i && showRow(i.l1Id, i.l2Id));
    setFilteredIntegrations(kineticIntegrations);
  }, [integrations, props.showAll, getL2, getRoomSyncFloorPlan]);

  useEffect(() => {
    const getData = async () => {
      setIsLoading(true);
      setPage(1);
      await loadIntegrations(selectedNetworkId);
      setIsLoading(false);
    };

    getData();
  }, [selectedNetworkId, setPage, loadIntegrations]);

  useEffect(() => {
    setPages(pages);
  }, [pages.length]);

  return (
    <EnhancedTableBody colSpan={9} isLoading={isLoading}>
      {selectedRows.map((row) => (
        <IntegrationTableKineticBodyRow {...row} showAll={props.showAll} key={row.id} />
      ))}
    </EnhancedTableBody>
  );
};
